import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { Labels } from '../../constants/labels-constant';
import { Loader } from '../../loader/loader';
import {
  ChangeCandidate,
  ContentsNode,
  DecisionAction,
  ReviewSection,
  isVerifyFlag,
} from '../../models/v2models/review-state';
import { ReviewStateService } from '../../services/review-state.service';
import { ReportV2Details, ReportV2Service } from '../../services/report-v2.service';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { SnackbarService } from '../../services/snackbar.service';
import { ChangeDetailPopup } from './change-detail-popup/change-detail-popup';
import { ReportStatus } from '../../models/report';

/** Which metric tile is filtering the section list, if any. */
export type SectionFilter = 'pending' | 'reviewed' | 'unchanged' | 'flagged' | null;

@Component({
  selector: 'app-review-refine',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, Loader, ChangeDetailPopup],
  templateUrl: './review-refine.html',
  styleUrl: './review-refine.scss',
})
export class ReviewRefine implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private reviewStateService = inject(ReviewStateService);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);
  private authService = inject(AuthService);

  labels = Labels;

  /** Apply is hidden for now — flip to true to bring the button back (US-15-01). */
  readonly showApply = false;

  /** Section-level manual editing is hidden for now — flip to true to restore it (US-14-01). */
  readonly showSectionEdit = false;

  reportId = 0;
  isLoading = false;
  isApplying = false;
  private currentUserName = '';

  /** Sections with their change candidates, from GET /Reports/{reportId}/review. */
  readonly sections = signal<ReviewSection[]>([]);
  readonly isLoaded = signal(false);
  /** Report header from GET /Reports/{reportId} — identity fields and server-side counters. */
  readonly header = signal<ReportV2Details | null>(null);
  readonly activeSectionId = signal<number | null>(null);

  /** Section currently open in the plain-text editor, and its working copy (US-14-01). */
  readonly editingSectionId = signal<number | null>(null);
  sectionDraft = '';

  /** Section open in the detail popup (screen 04), held by id so it tracks state updates. */
  readonly detailSectionId = signal<number | null>(null);
  /** Opens the popup straight into the whole-section editor. */
  readonly detailStartsInSectionEdit = signal(false);

  /** Metric tile currently used as a filter over the section list. */
  readonly activeFilter = signal<SectionFilter>(null);
  /** Accept actions currently waiting on the decision API, keyed by change id. */
  readonly acceptingChangeIds = signal<readonly number[]>([]);

  readonly visibleSections = computed<ReviewSection[]>(() => {
    const sections = this.sections();
    switch (this.activeFilter()) {
      case 'pending':
        return sections.filter((section) =>
          section.changes.some((change) => !isVerifyFlag(change) && change.status === 'Pending'),
        );
      case 'reviewed':
        // Has candidates and none still pending — every change decided (Accepted/Declined/Edited).
        return sections.filter(
          (section) =>
            section.changed &&
            !section.changes.some(
              (change) => !isVerifyFlag(change) && change.status === 'Pending',
            ),
        );
      case 'unchanged':
        return sections.filter((section) => !section.changed);
      case 'flagged':
        return sections.filter((section) =>
          section.changes.some((change) => this.needsValue(change)),
        );
      default:
        return sections;
    }
  });

  readonly detailSection = computed<ReviewSection | null>(() => {
    const sectionId = this.detailSectionId();
    return this.sections().find((section) => section.id === sectionId) ?? null;
  });

  /** The section body as plain text, for the popup's whole-section editor. */
  readonly detailSectionText = computed(() => {
    const section = this.detailSection();
    return section ? this.toEditableText(section.contentHtml) : '';
  });

  /**
   * Contents rail: sections nested to arbitrary depth by parentSectionId. It tracks the filter,
   * and a sub-section whose parent is filtered out is promoted to the top rather than lost.
   */
  readonly contents = computed<ContentsNode[]>(() => {
    const all = [...this.visibleSections()].sort((a, b) => a.sectionSequence - b.sectionSequence);
    const visibleIds = new Set(all.map((section) => section.id));
    const build = (parentId: number | null): ContentsNode[] =>
      all
        .filter((section) => section.parentSectionId === parentId)
        .map((section) => ({ section, children: build(section.id) }));
    // Roots are sections with no parent, or whose parent was filtered out (promoted).
    const roots = all.filter(
      (section) => section.parentSectionId === null || !visibleIds.has(section.parentSectionId),
    );
    return roots.map((section) => ({ section, children: build(section.id) }));
  });

  /** Verify flags are excluded: they propose nothing, so they are neither changes nor pending. */
  readonly allChanges = computed<ChangeCandidate[]>(() =>
    this.sections().flatMap((section) => section.changes.filter((c) => !isVerifyFlag(c))),
  );

  /** Distinct files referenced by any provenance entry. */
  readonly dataSourceCount = computed(
    () =>
      new Set(
        this.allChanges()
          .flatMap((change) => change.provenance ?? change.provenances ?? [])
          .map((source) => source.fileName)
          .filter(Boolean),
      ).size,
  );

  // Metrics and progress are derived from the section data rather than read straight off the
  // payload, so the header stays true as the reviewer decides (US-9-02, US-13-03).
  readonly metrics = computed(() => {
    const sections = this.sections();
    const header = this.header();

    // Before the sections arrive, show the server's counters. Once they are here the tiles are
    // derived from them, because the tiles also filter the list and the two must agree.
    if (!sections.length) {
      return {
        pending: 0,
        reviewed: 0,
        unchanged: header?.unchangedSections ?? 0,
        flagged: 0,
      };
    }

    return {
      pending: sections.filter((section) =>
        section.changes.some((change) => !isVerifyFlag(change) && change.status === 'Pending'),
      ).length,
      // Sections with candidates and nothing left pending — the partner of Pending, so together
      // with Unchanged the three buckets partition every section exactly once.
      reviewed: sections.filter(
        (section) =>
          section.changed &&
          !section.changes.some(
            (change) => !isVerifyFlag(change) && change.status === 'Pending',
          ),
      ).length,
      unchanged: sections.filter((section) => !section.changed).length,
      // Sections with a flag, not the count of flagged changes — matches how the tile filters
      // the list, and what "Showing N of M sections" reports once it is active.
      flagged: sections.filter((section) =>
        section.changes.some((change) => this.needsValue(change)),
      ).length,
    };
  });

  readonly progress = computed(() => {
    const changes = this.allChanges();
    // A saved edit is an applied decision: the server folds Edited into its Accepted counter and
    // the apply step applies Edited candidates, so it is grouped with Accepted here.
    const header = this.header();

    if (!changes.length && header) {
      const accepted = header.accepted ?? 0;
      const declined = header.declined ?? 0;
      const pending = header.pending ?? 0;
      return {
        total: accepted + declined + pending,
        accepted,
        declined,
        pending,
        decided: accepted + declined,
      };
    }

    const accepted = changes.filter(
      (change) => change.status === 'Accepted' || change.status === 'Edited',
    ).length;
    const declined = changes.filter((change) => change.status === 'Declined').length;
    return {
      total: changes.length,
      accepted,
      declined,
      pending: changes.length - accepted - declined,
      decided: accepted + declined,
    };
  });

  readonly decidedPercent = computed(() => {
    const { total, decided } = this.progress();
    return total === 0 ? 0 : Math.round((decided / total) * 100);
  });

  readonly acceptedPercent = computed(() => {
    const { total, accepted } = this.progress();
    return total === 0 ? 0 : Math.round((accepted / total) * 100);
  });

  readonly allDecided = computed(() => {
    const { total, pending } = this.progress();
    return total > 0 && pending === 0;
  });

  ngOnInit(): void {
    this.authService.getCurrentAccount().then((account) => {
      this.currentUserName = account?.name || account?.username || '';
    });

    this.route.queryParams.subscribe((params) => {
      this.reportId = Number(params['reportId']) || 0;
      this.loadHeader();
      this.loadReviewState();
    });
  }

  private loadHeader(): void {
    this.reportV2Service.getReportDetails(this.reportId).subscribe({
      next: (response) => {
        if (response.success) {
          this.header.set(response.data);
          if(response.data?.status !== ReportStatus.Review && response.data?.status !== ReportStatus.Generated && response.data?.status !== ReportStatus.Download) {
            this.router.navigate(['/reports']);
          }

          // Status transition to Review should happen only from Generated (2).
          if (response.data?.status === ReportStatus.Generated || response.data?.status === ReportStatus.Download) {
            this.updateReportStatus(this.reportId);
          }
        }
      },
      // The header is supporting detail — a failure here must not block the review itself.
      error: () => this.header.set(null),
    });
  }

  private updateReportStatus(reportId: number) {
    this.reportV2Service.updateReportStatus(reportId, { status: ReportStatus.Review }).subscribe({
      next: (response) => {
        if (response.success) {
          return;
        }

        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: response.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      error: (err) => {
        const errorMessage =
          err?.error?.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG;
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: errorMessage,
        });
      },
    });
  }

  private loadReviewState(preferredSectionId?: number): void {
    this.isLoading = true;
    this.reviewStateService.getReviewSections(this.reportId).subscribe({
      next: (sections) => {
        this.sections.set(sections);
        const targetSectionId =
          preferredSectionId ?? this.activeSectionId() ?? this.detailSectionId() ?? sections[0]?.id ?? null;
        const exists = targetSectionId != null && sections.some((section) => section.id === targetSectionId);
        this.activeSectionId.set(exists ? targetSectionId : (sections[0]?.id ?? null));
        this.isLoaded.set(true);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.isLoaded.set(true);
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: Labels.REVIEW_REFINE.TOASTER.LOAD_FAILED,
        });
      },
    });
  }

  // ---------------------------------------------------------------- metric filters

  /** Clicking the active tile again clears the filter. */
  toggleFilter(filter: Exclude<SectionFilter, null>): void {
    this.activeFilter.update((current) => (current === filter ? null : filter));
  }

  clearFilter(): void {
    this.activeFilter.set(null);
  }

  // ---------------------------------------------------------------- contents rail

  selectSection(sectionId: number): void {
    this.activeSectionId.set(sectionId);
    document
      .getElementById('reviewSection-' + sectionId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /** Unchanged sections are green; changed sections are orange in the contents rail. */
  sectionDotClass(section: ReviewSection): string {
    if (!section.changed) return 'dot-unchanged';
    return 'dot-changed';
  }

  pendingCount(section: ReviewSection): number {
    return this.proposedChanges(section).filter((change) => change.status === 'Pending').length;
  }

  /** Accepted and edited both count as confirmed — an edit is a confirmation with a revised value. */
  confirmedCount(section: ReviewSection): number {
    return this.proposedChanges(section).filter(
      (change) => change.status === 'Accepted' || change.status === 'Edited',
    ).length;
  }

  declinedCount(section: ReviewSection): number {
    return this.proposedChanges(section).filter((change) => change.status === 'Declined').length;
  }

  private proposedChanges(section: ReviewSection): ChangeCandidate[] {
    return section.changes.filter((change) => !isVerifyFlag(change));
  }

  /**
   * Flagged by the detector as needing a look even though nothing changed. The reason sits on
   * the candidate, so the section is flagged when any of its candidates carries it.
   */
  isReviewRequired(section: ReviewSection): boolean {
    return section.changes.some(
      (change) =>
        (change.candidateType ?? '').toUpperCase() === 'VERIFY_FLAG' &&
        (change.triggerReason ?? '').toUpperCase() === 'UNCHANGED_REVIEW_REQUIRED',
    );
  }

  /** Marks that the section carries a manual edit, whatever its decision state. */
  editedCount(section: ReviewSection): number {
    return section.changes.filter((change) => this.isLocalEdit(change)).length;
  }

  private isAcceptedLike(change: ChangeCandidate): boolean {
    return change.status === 'Accepted' || change.status === 'Edited';
  }

  // ---------------------------------------------------------------- decisions (US-13-01)

  /** The value that would be written into the document if this change is confirmed. */
  effectiveValue(change: ChangeCandidate): string {
    // Edited values must win over the original proposal — a saved edit only patches editedHtml,
    // so proposedText coming first would mask it and mis-flag the change as needing a value.
    return (
      change.editedText ??
      change.editedHtml ??
      change.proposedText ??
      change.proposedHtml ??
      ''
    );
  }

  /** True when the detector left the value empty for the reviewer to supply (US-7-03). */
  needsValue(change: ChangeCandidate): boolean {
    return this.effectiveValue(change).trim().length === 0;
  }

  confirm(change: ChangeCandidate): void {
    if (this.isLocalEdit(change)) {
      this.patchChange(change.id, { status: 'Accepted' });
      this.notifyDecision('Accepted');
      return;
    }

    if (this.needsValue(change)) {
      this.snackbar.showCustom({
        type: 'warning',
        title: ValidationMessagesTexts.TOASTER.WARNING.TITLE,
        message: Labels.REVIEW_REFINE.NEEDS_VALUE,
      });
      return;
    }
    this.acceptingChangeIds.update((ids) =>
      ids.includes(change.id) ? ids : [...ids, change.id],
    );
    this.recordDecision(change, 'Accepted', change.editedHtml ?? null, () =>
      this.notifyDecision('Accepted'),
      () => this.clearAcceptingChange(change.id),
    );
  }

  decline(change: ChangeCandidate): void {
    if (this.isLocalEdit(change)) {
      this.revertManualEdit(change);
      this.notifyDecision('Declined');
      return;
    }
    this.recordDecision(change, 'Declined', change.editedHtml ?? null, () =>
      this.notifyDecision('Declined'),
    );
  }

  /** Confirms the verdict landed — the row's own state change is easy to miss mid-list. */
  private notifyDecision(action: DecisionAction): void {
    this.snackbar.showCustom({
      type: 'success',
      title: ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
      message:
        action === 'Declined'
          ? Labels.REVIEW_REFINE.TOASTER.CHANGE_DECLINED
          : Labels.REVIEW_REFINE.TOASTER.CHANGE_ACCEPTED,
    });
  }

  /** Declining a manual edit puts the section text back as it was before the edit. */
  private revertManualEdit(change: ChangeCandidate): void {
    const originalContent = change.originalText ?? change.originalHtml ?? '';
    this.updateSection(change.sectionId, (current) => ({
      ...current,
      contentHtml: originalContent,
      previousContentHtml: originalContent,
    }));
    this.patchChange(change.id, { status: 'Declined' });
  }

  private recordDecision(
    change: ChangeCandidate,
    action: DecisionAction,
    editedHtml: string | null,
    onSaved?: () => void,
    onFinished?: () => void,
  ): void {
    this.reviewStateService
      .recordDecision(
        this.reportId,
        {
          sectionId: change.sectionId,
          changeId: change.id,
          action,
          editedHtml,
        },
        this.currentUserName,
      )
      .pipe(finalize(() => onFinished?.()))
      .subscribe({
        // The server's status is authoritative — a decline that the API records as something
        // else must not show as declined here.
        next: (result) => {
          const savedText = result.editedHtml ?? editedHtml;
          this.patchChange(change.id, {
            status: result.status,
            editedText: savedText,
            editedHtml: savedText,
          });
          onSaved?.();
        },
        error: () =>
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: Labels.REVIEW_REFINE.TOASTER.DECISION_FAILED,
          }),
      });
  }

  private clearAcceptingChange(changeId: number): void {
    this.acceptingChangeIds.update((ids) => ids.filter((id) => id !== changeId));
  }

  // ---------------------------------------------------------------- manual section edit (US-14-01)

  startSectionEdit(section: ReviewSection): void {
    this.isLoading = true;
    this.reportV2Service.getSectionEditText(this.reportId, section.id).subscribe({
      next: (response) => {
        if (!response.success || !response.data) {
          this.isLoading = false;
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: response.message || Labels.REVIEW_REFINE.TOASTER.SECTION_LOAD_FAILED,
          });
          return;
        }

        // Store version in the section for later use in save
        section.version = response.data.version;
        // Bind the text from the API response
        this.sectionDraft = response.data.text || this.toEditableText(section.contentHtml);
        this.editingSectionId.set(section.id);
        this.activeSectionId.set(section.id);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: Labels.REVIEW_REFINE.TOASTER.SECTION_LOAD_FAILED,
        });
      },
    });
  }

  cancelSectionEdit(): void {
    this.editingSectionId.set(null);
    this.sectionDraft = '';
  }

  saveSectionEdit(section: ReviewSection): void {
    if (!this.sectionDraft.trim()) {
      this.snackbar.showCustom({
        type: 'warning',
        title: ValidationMessagesTexts.TOASTER.WARNING.TITLE,
        message: Labels.REVIEW_REFINE.TOASTER.SECTION_REQUIRES_TEXT,
      });
      return;
    }

    const contentHtml = this.toContentHtml(this.sectionDraft);
    if (contentHtml === section.contentHtml) {
      this.cancelSectionEdit();
      return;
    }

    // Send the plain draft, NOT contentHtml. For a prior-report report the API forwards
    // this to the agents service, which diffs it against the document's own plain text —
    // wrapping it in <p> tags would make every line read as changed and write the markup
    // itself into the Word file. contentHtml is kept only for the local display copy below.
    this.reportV2Service.updateSectionContent(this.reportId, section.id, this.sectionDraft, section.version).subscribe({
      next: (response) => {
        if (!response.success) {
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: response.message || Labels.REVIEW_REFINE.TOASTER.SECTION_SAVE_FAILED,
          });
          return;
        }

        this.captureManualEdit(section, contentHtml);
        this.cancelSectionEdit();
        this.snackbar.showCustom({
          type: 'success',
          title: ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
          message: Labels.REVIEW_REFINE.TOASTER.SECTION_SAVED,
        });
      },
      error: (err) => {
        // 409 means the document moved on since this section was opened. Keep the editor
        // open so the typed text is not lost, and ask for a reload.
        const isStale = err?.status === 409;
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: isStale
            ? err?.error?.message || Labels.REVIEW_REFINE.TOASTER.SECTION_RELOAD_REQUIRED
            : err?.error?.message || Labels.REVIEW_REFINE.TOASTER.SECTION_SAVE_FAILED,
        });
        if (isStale) {
          this.editingSectionId.set(section.id);
        }
      },
    });
  }

  /**
   * A manual edit is recorded as its own reviewable change candidate — already decided by the
   * person who made it, and carried through apply like any other confirmed change (US-14-02).
   */
  private captureManualEdit(section: ReviewSection, contentHtml: string): void {
    const manualChange: ChangeCandidate = {
      id: Date.now(),
      sectionId: section.id,
      changeType: 'Manual',
      matchKind: 'Manual',
      candidateType: 'MANUAL',
      confidence: 1,
      isDateFlagged: false,
      // A manual edit is the reviewer's own work, not something the detector raised.
      triggerReason: null,
      status: 'Pending',
      isLocal: true,
      originalHtml: section.contentHtml,
      proposedHtml: contentHtml,
      editedHtml: contentHtml,
      provenance: [],
    };

    // The edit becomes the working text of the section, so the body and every subsequent
    // before/after preview are built from what the reviewer actually wrote.
    this.updateSection(section.id, (current) => ({
      ...current,
      changed: true,
      contentHtml,
      previousContentHtml: contentHtml,
      changeCount: current.changeCount + 1,
      changes: [...current.changes, manualChange],
    }));

    // No decision is posted for a manual edit: its change id is client-side, so the decision
    // endpoint has nothing to record against. It is persisted by the section save instead,
    // which is itself still stubbed pending the manual-edit API (US-14-02).
  }

  /** Section markup → plain text for the textarea, with paragraphs kept as blank lines. */
  private toEditableText(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html.replace(/<\/p>/gi, '</p>\n\n').replace(/<br\s*\/?>/gi, '\n');
    return (container.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  /** Textarea text → section markup: blank lines become paragraphs, single ones line breaks. */
  private toContentHtml(text: string): string {
    return text
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${this.escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
      .join('');
  }

  private escapeHtml(value: string): string {
    const container = document.createElement('div');
    container.textContent = value;
    return container.innerHTML;
  }

  // ---------------------------------------------------------------- before/after preview

  /** A manual edit made in this browser: no server-side id, so decisions stay local. */
  isLocalEdit(change: ChangeCandidate): boolean {
    return change.isLocal === true;
  }

  /** Opens screen 04 for a whole section — one preview per section, not per change. */
  openSectionDetail(section: ReviewSection, startInSectionEdit = false): void {
    this.detailSectionId.set(section.id);
    this.detailStartsInSectionEdit.set(startInSectionEdit);
  }

  /** Whole-section rewrite saved from the popup (US-14-01). */
  saveSectionFromDetail(section: ReviewSection, text: string): void {
    this.sectionDraft = text;
    this.saveSectionEdit(section);
  }

  /**
   * After saving a whole-section edit from the popup, refresh the review data so newly produced
   * candidates (Decline/Accept rows) appear in-place without closing the dialog.
   */
  onSectionSavedFromDetail(sectionId: number): void {
    this.detailSectionId.set(sectionId);
    this.detailStartsInSectionEdit.set(false);
    this.loadReviewState(sectionId);
  }

  closeChangeDetail(): void {
    this.detailSectionId.set(null);
    this.detailStartsInSectionEdit.set(false);
  }

  /** Decisions are made in the popup and it stays open — the section's other edits are there. */
  confirmFromDetail(change: ChangeCandidate): void {
    this.confirm(change);
  }

  declineFromDetail(change: ChangeCandidate): void {
    this.decline(change);
  }

  /** Saving from the popup stores the value and accepts the change in the same action. */
  saveEditFromDetail(change: ChangeCandidate, editedHtml: string): void {
    this.recordDecision(change, 'Accepted', editedHtml, () =>
      this.snackbar.showCustom({
        type: 'success',
        title: ValidationMessagesTexts.TOASTER.SUCCESS.TITLE,
        message: Labels.REVIEW_REFINE.TOASTER.CHANGE_ACCEPTED,
      }),
    );
  }

  // ---------------------------------------------------------------- page actions

  /** Applies only the accepted set; pending candidates are never applied silently (US-15-03). */
  applyConfirmedChanges(): void {
    const changes = this.allChanges();
    // The API returns no counts, so the summary is built from the decisions on screen — the
    // same ones already posted to the server.
    // Edited candidates are applied by the server just like Accepted ones, so they count as
    // applied here — only truly Pending changes are left out.
    const accepted = changes.filter(
      (change) => change.status === 'Accepted' || change.status === 'Edited',
    ).length;
    const undecided = changes.filter((change) => change.status === 'Pending').length;

    this.isApplying = true;
    this.reviewStateService.applyEdits(this.reportId).subscribe({
      next: () => {
        this.isApplying = false;
        this.snackbar.showCustom({
          type: undecided > 0 ? 'warning' : 'success',
          title: Labels.REVIEW_REFINE.TOASTER.APPLY_TITLE,
          message: this.applyResultMessage(accepted, undecided),
        });
      },
      error: () => {
        this.isApplying = false;
        this.snackbar.showCustom({
          type: 'error',
          title: Labels.REVIEW_REFINE.TOASTER.APPLY_FAILED_TITLE,
          message: Labels.REVIEW_REFINE.TOASTER.APPLY_FAILED,
        });
      },
    });
  }

  /**
   * Declined changes go unmentioned — those were decided on purpose. Only undecided ones matter,
   * because they are the gap between the screen and the file (US-15-03).
   */
  private applyResultMessage(applied: number, undecided: number): string {
    const message = Labels.REVIEW_REFINE.TOASTER.APPLY_RESULT.replace(
      '{applied}',
      String(applied),
    ).replace('{appliedNoun}', this.changeNoun(applied));

    if (undecided === 0) return message;

    // Singular needs its own sentence — the verb has to agree, not just the noun.
    const leftOut =
      undecided === 1
        ? Labels.REVIEW_REFINE.TOASTER.APPLY_LEFT_OUT_ONE
        : Labels.REVIEW_REFINE.TOASTER.APPLY_LEFT_OUT_MANY.replace('{pending}', String(undecided));

    return `${message} ${leftOut}`;
  }

  private changeNoun(count: number): string {
    return count === 1 ? Labels.REVIEW_REFINE.CHANGE_ONE : Labels.REVIEW_REFINE.CHANGE_MANY;
  }

  /**
   * Advances to compile & export. Deliberately not gated on every change being decided:
   * ordering appendices is unrelated work, and undecided changes are excluded server-side
   * anyway (US-15-03). The compile screen warns about anything still pending.
   */
  compileAndExport(): void {
    this.router.navigate(['/report-compile'], { queryParams: { reportId: this.reportId } });
  }

  close(): void {
    this.router.navigate(['/reports']);
  }

  // ---------------------------------------------------------------- helpers

  trackChange = (_: number, change: ChangeCandidate) => change.id;
  trackSection = (_: number, section: ReviewSection) => section.id;

  private patchChange(changeId: number, patch: Partial<ChangeCandidate>): void {
    this.sections.update((sections) =>
      sections.map((section) => {
        if (!section.changes.some((change) => change.id === changeId)) return section;
        const changes = section.changes.map((change) =>
          change.id === changeId ? { ...change, ...patch } : change,
        );
        return {
          ...section,
          changes,
          pendingCount: changes.filter((change) => change.status === 'Pending').length,
        };
      }),
    );
  }

  private updateSection(
    sectionId: number,
    project: (section: ReviewSection) => ReviewSection,
  ): void {
    this.sections.update((sections) =>
      sections.map((section) => (section.id === sectionId ? project(section) : section)),
    );
  }
}
